import React from "react";
import { SHOT_TYPES, CAMERA_MOVES } from "../constants/storyboard";
import {
  NOTES_FONT, NOTES_INK, NOTES_MUTED, NOTES_RULE, NOTES_HEADER_HEIGHT,
  NOTES_LABEL_SIZE, NOTES_TEXT_SIZE, NOTES_LINE_HEIGHT, notesLayout,
} from "../utils/shotNotes";
import { useEditorStoreApi } from "../store/editorStoreSelectors";

const fieldReset = {
  border: "none",
  background: "transparent",
  color: NOTES_INK,
  fontFamily: NOTES_FONT,
  outline: "none",
  margin: 0,
  padding: 0,
};

// Shows the short code ("MS", "Push in") on the board; the native menu,
// stacked invisibly on top, lists the full names.
const PickField = ({ label, title, value, placeholder, options, onChange }) => (
  <span style={{ position: "relative", display: "inline-flex", alignItems: "center" }}>
    <span
      aria-hidden="true"
      style={{
        fontSize: 9.5, fontWeight: 700, whiteSpace: "nowrap",
        color: value ? NOTES_INK : NOTES_MUTED,
        borderBottom: value ? "none" : `1px dotted ${NOTES_MUTED}`,
      }}
    >
      {value || placeholder}
    </span>
    <select
      aria-label={label}
      title={title}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{ ...fieldReset, position: "absolute", inset: 0, opacity: 0, cursor: "pointer", fontSize: 12 }}
    >
      {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  </span>
);

/**
 * A frame's notes, typed straight onto the board: shot type, camera move,
 * duration, action, and dialogue. The page renderer draws the same layout
 * (utils/shotNotes) for thumbnails and exports.
 */
const ShotNotes = ({ cell, shotNumber, compact, onChange }) => {
  const storeApi = useEditorStoreApi();
  const box = cell.notesBox;
  if (!box) return null;
  const shot = cell.shot || {};
  const { sections } = notesLayout(box, { compact });

  const update = (updates) => onChange(cell.id, { shot: { ...shot, ...updates } });
  // A whole typing session is one undo step
  const typingHandlers = {
    onFocus: () => storeApi.getState().beginGesture(),
    onBlur: () => storeApi.getState().endGesture(),
  };
  const stop = (event) => event.stopPropagation();

  return (
    <div
      data-shot-notes={cell.id}
      onClick={stop}
      onPointerDown={stop}
      onDoubleClick={stop}
      style={{
        position: "absolute",
        left: box.x,
        top: box.y,
        width: box.width,
        height: box.height,
        boxSizing: "border-box",
        border: `1px solid ${NOTES_RULE}`,
        fontFamily: NOTES_FONT,
        color: NOTES_INK,
        zIndex: 5,
      }}
    >
      <div
        style={{
          position: "absolute", left: 0, top: 0, right: 0, height: NOTES_HEADER_HEIGHT,
          borderBottom: `1px solid ${NOTES_RULE}`, boxSizing: "border-box",
          display: "flex", alignItems: "center", gap: 4, padding: "0 5px",
        }}
      >
        <span style={{ fontSize: 9.5, fontWeight: 700, letterSpacing: 0.6, whiteSpace: "nowrap" }}>
          SHOT {shotNumber}
        </span>
        <span style={{ flex: 1 }} />
        <PickField
          label={`Shot ${shotNumber} type`}
          title="Shot type"
          value={shot.shotType || ""}
          placeholder="Shot"
          options={SHOT_TYPES}
          onChange={(value) => update({ shotType: value })}
        />
        <PickField
          label={`Shot ${shotNumber} camera move`}
          title="Camera move"
          value={shot.move || ""}
          placeholder="Move"
          options={CAMERA_MOVES}
          onChange={(value) => update({ move: value })}
        />
        <label style={{ display: "flex", alignItems: "center", fontSize: 9.5, fontWeight: 700, color: NOTES_MUTED }}>
          <input
            type="number"
            aria-label={`Shot ${shotNumber} duration in seconds`}
            title="Duration (seconds)"
            min={0}
            max={600}
            step={0.5}
            placeholder="–"
            value={shot.duration ?? ""}
            onChange={(e) => {
              const value = e.target.value === "" ? null : Math.max(0, Number(e.target.value));
              update({ duration: value });
            }}
            {...typingHandlers}
            style={{ ...fieldReset, width: 26, fontSize: 9.5, fontWeight: 700, textAlign: "right", MozAppearance: "textfield" }}
          />
          s
        </label>
      </div>

      {sections.map((section) => (
        <React.Fragment key={section.field}>
          {section.showLabel && (
            <div
              aria-hidden="true"
              style={{
                position: "absolute", left: section.x, top: section.y + 2,
                fontSize: NOTES_LABEL_SIZE, letterSpacing: 0.8, textTransform: "uppercase",
                color: NOTES_MUTED, pointerEvents: "none",
              }}
            >
              {section.label}
            </div>
          )}
          <textarea
            aria-label={`Shot ${shotNumber} ${section.label.toLowerCase()}`}
            placeholder={section.field === "action" ? "What happens in this shot…" : "Lines, VO, music, SFX…"}
            value={shot[section.field] || ""}
            onChange={(e) => update({ [section.field]: e.target.value })}
            {...typingHandlers}
            className="striply-shot-notes-text"
            style={{
              ...fieldReset,
              position: "absolute",
              left: section.x,
              top: section.textY,
              width: section.width,
              height: section.textHeight,
              fontSize: NOTES_TEXT_SIZE,
              lineHeight: NOTES_LINE_HEIGHT,
              resize: "none",
              overflow: "hidden",
            }}
          />
        </React.Fragment>
      ))}
    </div>
  );
};

export default ShotNotes;
